import { clientInstance } from '../../../services/graphql.service';

import { cancelTestDriveBooking as cancelTestDriveBookingQuery } from '../../../graphql/mutations';
import { configurations } from '../../../graphql/queries';
import { useStore } from '../../../composables/store.composable';
import useDealerLocator from '../../../composables/dealer-locator.composable';

import {
  AppointmentCalendar,
  BookingAppointmentCancellationInput,
  MutationCancelTestDriveBookingArgs,
  QueryConfigurationsArgs,
  UiConfiguration,
} from '../../../graphql/schema';
import GenericStore from '../../../models/store/generic-store.model';

import DateService from '../../../services/date.service';
import UtilsService from '../../../services/utils.service';
import TrackingService from '../../../services/tracking.service';

const { client } = clientInstance();

/* ==================== ACTIONS ==================== */
const getQueryVariable = (name: string): string | null => {
  const queryStart = window.location.hash.indexOf('?') + 1;
  const query = window.location.hash.substring(
    queryStart,
    window.location.hash.length
  );
  const vars = query.split('&');
  for (let i = 0; i < vars.length; i += 1) {
    const pair = vars[i].split('=');
    if (decodeURIComponent(pair[0]) === name) {
      return decodeURIComponent(pair[1]);
    }
  }
  return null;
};

const fetchConfigurations = async ({
  state: {
    locale: { countryCode, language },
  },
  commit,
  dispatch,
}: GenericStore): Promise<void> => {
  const { state, getter } = useStore();
  const { getSearchProfileName } = useDealerLocator();
  const context = getQueryVariable('context') || 'OWPC';
  const variables: QueryConfigurationsArgs = {
    countryCode,
    language,
    context,
  };

  const configs = await client.query(configurations, variables).toPromise();
  const today = new Date();
  const firstDate = new Date();
  const { configurations: config }: { configurations: UiConfiguration } =
    UtilsService.cleanTypenames(configs.data);
  const {
    labels,
    links,
    booking,
    aemFormsResources,
    aemFormsHandlerConfig,
    pickMeUpFromHome,
    cancellationForm,
    dealerSearch,
    calendar,
    summary,
    contactForm,
    oneDoc,
    modelSeriesMapper,
    modelSelection,
    sharedNotifications,
    footNotes,
  } = config;
  const { initialUnavailableDays, weekStartsOn = 'sun' } =
    calendar! as AppointmentCalendar & { weekStartsOn: string };

  firstDate.setDate(today.getDate() + initialUnavailableDays);

  // set initial date is not considering weekend, so this add days if it is sunday or saturday
  if (firstDate.getDay() === 0) {
    firstDate.setDate(firstDate.getDate() + 1);
  } else if (firstDate.getDay() === 6) {
    firstDate.setDate(firstDate.getDate() + 2);
  }

  const notificationsConfig = sharedNotifications;

  commit('selectedDay', firstDate);
  commit('calendarWeekDayStart', weekStartsOn);
  commit('calendarStartDate', DateService.firstDayOfMonth());
  commit('labels', labels);
  commit('links', links);
  commit('rescheduleActive', booking?.rescheduleActive || false);
  commit('bookingActive', booking?.bookingActive || false);
  commit('market', getCountryProxy(countryCode));
  commit('aemFormsResources', aemFormsResources?.frontend?.endpoint);
  commit('aemFormsHandlerConfig', aemFormsHandlerConfig?.frontend);
  commit('pickMeUpFromHome', pickMeUpFromHome);
  commit('cancellationFormConfig', cancellationForm?.frontend);
  commit('initialUnavailableDays', initialUnavailableDays);
  commit('firstAvailableDate', firstDate);
  commit('dayTimePeriods', calendar?.timePicker?.dayTimePeriods);
  commit('dealerSearch', dealerSearch?.frontend);
  commit('teaserVisible', summary?.frontend.teaser.visible);
  commit('formConfig', contactForm?.frontend);
  commit('oneDoc', oneDoc);
  commit('modelSeriesMapper', modelSeriesMapper?.frontend);
  commit('sharedNotifications', notificationsConfig);
  commit('footNotes', footNotes?.frontend);
  commit('trackingSplitters', booking?.trackingInfo);
  //TODO If in the future this behavior needs to be configurable per market, just change it here
  commit('shouldSkipNoResultsPage', true);

  let modelSearchProfileMapping;
  if (modelSelection) {
    const { frontend } = modelSelection;
    const { modelMapping } = frontend;
    modelSearchProfileMapping = modelMapping;
  }
  commit('modelMapping', modelSearchProfileMapping);

  commit(
    'isCo2EmissionAndFuelConsumptionVisibile',
    modelSelection?.carbonFootprint?.co2EmissionAndFuelConsumption?.visible ??
      false
  );

  commit(
    'isEnergyEfficiencyVisibile',
    modelSelection?.carbonFootprint?.energyEfficiency?.visible ?? false
  );

  const searchProfile = getSearchProfileName(
    getter.dealerSearchConfig,
    state.configuration.modelMapping,
    state.otbcFlow
  );
  commit('searchProfileName', searchProfile.searchProfileName);

  const dealerRadius = dealerSearch?.frontend.dealerRadiusSearchOptions;

  if (dealerRadius) {
    const length =
      (dealerRadius.maxRadius - dealerRadius.minRadius) / dealerRadius.step + 1;

    const arr = Array.from(
      { length },
      (_, i) => dealerRadius.minRadius + i * dealerRadius.step
    );
    commit('dealerRadius', dealerRadius);
    commit('dealerRadiusArray', arr);
    dispatch('selectLocationRadius', {
      locationRadius: `${arr[Math.floor((arr.length - 1) / 2)]}${
        dealerRadius.unit
      }`,
    });
  }
  if (TrackingService.getUtmParameters())
    await commit('utmParameters', TrackingService.getUtmParameters());
};

const sendForm = async (
  {
    state: {
      locale: { countryCode, language },
    },
    dispatch,
  }: GenericStore,
  { id, cancellationMessage }: any
): Promise<any> => {
  const { state } = useStore();
  if (id) {
    const bookingAppointmentCancellation: BookingAppointmentCancellationInput =
      {
        id,
        countryCode,
        language,
        cancellationMessage,
        context: state.configuration.context,
      };

    return await dispatch(
      'cancelTestDriveBooking',
      bookingAppointmentCancellation
    );
  } else {
    throw new Error(`Invalid Booking ID`);
  }
};

const cancelTestDriveBooking = async (
  { commit: _ }: GenericStore,
  bookingAppointmentCancellation: BookingAppointmentCancellationInput
): Promise<any> => {
  const variables: MutationCancelTestDriveBookingArgs = {
    bookingAppointmentCancellation,
  };

  return await client
    .mutation(cancelTestDriveBookingQuery, variables)
    .toPromise()
    .then((response) => {
      if (response.data) {
        return UtilsService.cleanTypenames(response);
      } else if (response.error) {
        throw new Error(
          `Server error: `,
          UtilsService.cleanTypenames(response.error)
        );
      }
    });
};

const countryProxy: { [key: string]: string } = Object.freeze({
  FR: 'FX',
});

const getCountryProxy = (market: string): any => {
  return countryProxy[market] || market;
};

export const actions = {
  fetchConfigurations,
  sendForm,
  cancelTestDriveBooking,
};
